import { useTranslation } from "react-i18next";
import { ExternalLink, Gamepad2, Trash2 } from "lucide-react";

import { useEmdrSets } from "../../application/useEmdrSets";
import { boardPath } from "../../domain/boardPaths";
import { summarizeSets } from "../../domain/emdrSummary";
import EmdrRatingCharts from "./EmdrRatingCharts";

type Props = {
    sessionId: string;
};

function formatTime(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * The EMDR part of a session's record: every set that was rated, in a place
 * of its own.
 *
 * What the therapist saved from the call or the board is read back here —
 * the SUD curve, the closing VOC, and each set with its note — and can be
 * corrected by removing a set that was saved by mistake.
 */
export default function EmdrSessionPanel({ sessionId }: Props) {
    const { t } = useTranslation();
    const recorder = useEmdrSets(sessionId);
    const { sets } = recorder;
    const summary = summarizeSets(sets);

    const openBoard = () => window.open(boardPath(sessionId), "_blank", "noopener,noreferrer");

    return (
        <div className="flex flex-col gap-5" data-testid="emdr-session-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-content-secondary">{t("emdr.session.intro")}</p>
                <button
                    type="button"
                    onClick={openBoard}
                    className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-border-subtle px-3 text-sm font-medium text-content-primary hover:bg-surface-sunken"
                >
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    {t("emdr.session.openBoard")}
                </button>
            </div>

            {recorder.error && (
                <p role="alert" className="text-sm text-status-critical">
                    {t(`emdr.sets.errors.${recorder.error}`)}
                </p>
            )}

            {sets.length === 0 ? (
                <p className="rounded-xl bg-surface-sunken px-4 py-6 text-center text-sm text-content-tertiary">
                    {t("emdr.session.empty")}
                </p>
            ) : (
                <>
                    <dl className="grid grid-cols-3 gap-3">
                        <div className="rounded-xl bg-surface-sunken px-4 py-3">
                            <dt className="text-xs text-content-tertiary">{t("emdr.session.setsCount")}</dt>
                            <dd className="text-xl font-semibold tabular-nums text-content-primary">{summary.count}</dd>
                        </div>
                        <div className="rounded-xl bg-surface-sunken px-4 py-3">
                            <dt className="text-xs text-content-tertiary">{t("emdr.session.sudCourse")}</dt>
                            <dd className="text-xl font-semibold tabular-nums text-content-primary" data-testid="emdr-summary-sud">
                                {summary.firstSud === null ? "–" : `${summary.firstSud} → ${summary.lastSud}`}
                            </dd>
                        </div>
                        <div className="rounded-xl bg-surface-sunken px-4 py-3">
                            <dt className="text-xs text-content-tertiary">{t("emdr.session.lastVoc")}</dt>
                            <dd className="text-xl font-semibold tabular-nums text-content-primary" data-testid="emdr-summary-voc">
                                {summary.lastVoc ?? "–"}
                            </dd>
                        </div>
                    </dl>

                    <EmdrRatingCharts sets={sets} />

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <caption className="sr-only">{t("emdr.sets.listLabel")}</caption>
                            <thead className="text-xs text-content-tertiary">
                                <tr>
                                    <th scope="col" className="py-2 pr-3 font-medium">{t("emdr.session.columns.set")}</th>
                                    <th scope="col" className="py-2 pr-3 font-medium">{t("emdr.session.columns.passes")}</th>
                                    <th scope="col" className="py-2 pr-3 font-medium">{t("emdr.session.columns.stimulation")}</th>
                                    <th scope="col" className="py-2 pr-3 font-medium">SUD</th>
                                    <th scope="col" className="py-2 pr-3 font-medium">VOC</th>
                                    <th scope="col" className="py-2 pr-3 font-medium">{t("emdr.session.columns.note")}</th>
                                    <th scope="col" className="py-2 pr-3 font-medium">{t("emdr.session.columns.time")}</th>
                                    <th scope="col" className="py-2"><span className="sr-only">{t("emdr.sets.delete")}</span></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-subtle text-content-primary">
                                {sets.map((set) => (
                                    <tr key={set.id}>
                                        <td className="py-2 pr-3 tabular-nums">{set.set_number}</td>
                                        <td className="py-2 pr-3 tabular-nums">{set.passes}</td>
                                        <td className="py-2 pr-3 text-content-secondary">
                                            <span className="inline-flex items-center gap-1">
                                                {t(`emdr.mode.${set.mode}`)} · {t("emdr.speed.value", { count: set.cycles_per_minute })}
                                                {set.tactile && <Gamepad2 className="h-3.5 w-3.5" aria-label={t("emdr.tactile.label")} />}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-3 font-semibold tabular-nums">{set.sud ?? "–"}</td>
                                        <td className="py-2 pr-3 font-semibold tabular-nums">{set.voc ?? "–"}</td>
                                        <td className="max-w-xs py-2 pr-3 text-content-secondary">{set.note ?? ""}</td>
                                        <td className="py-2 pr-3 tabular-nums text-content-tertiary">{formatTime(set.recorded_at)}</td>
                                        <td className="py-2 text-right">
                                            <button
                                                type="button"
                                                onClick={() => void recorder.remove(set.id)}
                                                aria-label={t("emdr.sets.delete")}
                                                className="inline-flex size-9 items-center justify-center rounded-lg text-content-tertiary hover:bg-status-critical-tint hover:text-status-critical-on-tint"
                                            >
                                                <Trash2 className="h-4 w-4" aria-hidden="true" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
